import { useQuery } from '@tanstack/react-query';
import HtmlToReact from 'html-to-react';
import React from 'react';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import Table from 'react-bootstrap/Table';
import { useTranslation } from 'react-i18next';

import { useErrorMessage } from '../../errors';
import { uiHelpers } from '../../helpers';
import uiHelper from '../../helpers/ui.helper';
import { Category } from '../../py-generated/datalouder/stock/types';
import { stockService } from '../../services';
import { USStockProfile } from '../../shared/types';
import LoadingImage from '../general/LoadingImage';

const { Parser: HtmlToReactParser } = HtmlToReact;
const htmlToReactParser = HtmlToReactParser();

type RowData = [
  string,
  string | number | undefined,
  (value: string | number | Date) => string | number,
];

const mapper = ([title, value, formatter]: RowData) =>
  value === undefined ? null : (
    <tr key={title}>
      <th>{title}</th>
      <td>{formatter(value)}</td>
    </tr>
  );

const formatDummy = (value: string | number) => value;
const UPDATED_AT = 'Updated at';

interface Props {
  category: Category;
  ticker: string;
  language: string;
}

const HKProfile = ({ profile: { profile } }) => {
  const { t, i18n } = useTranslation();
  return (
    <>
      <Row>
        <Col>{profile.summary && <p>{profile.summary}</p>}</Col>
      </Row>
      <Row>
        <Col md={6}>
          <Table bordered size="sm" style={{ fontSize: '0.8rem' }}>
            <tbody>
              <tr>
                <th>{t('Total number of Issued Shares/Warrants/Units')}</th>
                <td>
                  {uiHelpers.formatLargeMoney(i18n, profile.amt_os)} (
                  {t('as at')} {profile.shares_issued_date})
                </td>
              </tr>
              <tr>
                <th>{t('Industry')}</th>
                <td>
                  {profile.hsic_ind_classification}
                  {' - '}
                  {profile.hsic_sub_sector_classification}
                </td>
              </tr>
              <tr>
                <th>{t('Listing Date')}</th>
                <td>{profile.listing_date}</td>
              </tr>
              <tr>
                <th>{t('Financial Year Ends')}</th>
                <td>{profile.fiscal_year_end}</td>
              </tr>
            </tbody>
          </Table>
        </Col>
        <Col md={6}>
          <Table bordered size="sm" style={{ fontSize: '0.8rem' }}>
            <tbody>
              <tr>
                <th>{t('Chairman')}</th>
                <td>{profile.chairman}</td>
              </tr>
              <tr>
                <th>{t('Principal Office')}</th>
                <td>{htmlToReactParser.parse(profile.office_address)}</td>
              </tr>
              <tr>
                <th>{t('Place of Incorporation')}</th>
                <td>{profile.incorpin}</td>
              </tr>
              <tr>
                <th>{t('Listing Category')}</th>
                <td>{profile.listing_category}</td>
              </tr>
              <tr>
                <th>{t('Registrar')}</th>
                <td>{profile.registrar}</td>
              </tr>
            </tbody>
          </Table>
        </Col>
      </Row>
    </>
  );
};

const USProfile: React.FC<{ profile: USStockProfile }> = ({
  profile: { profile, keyStats, financial },
}) => {
  const { t, i18n } = useTranslation();

  const formatDecimal = uiHelpers.formatDecimal;
  const formatLargeMoney = (value: number) =>
    uiHelpers.formatLargeMoney(i18n, value);
  const formatDatetime = uiHelpers.formatDateTime;

  const leftRows: RowData[] = [
    [
      t('Industry'),
      profile ? profile?.sector + ' - ' + profile?.industry : undefined,
      formatDummy,
    ],
    [t('Chairman'), profile?.ceo, formatDummy],
    [t('Shares outstanding'), keyStats?.sharesOutstanding, formatLargeMoney],
    [t('PE Ratio'), keyStats?.peRatio, formatDecimal],
    [t('Beta'), keyStats?.beta, formatDecimal],
    [t(UPDATED_AT), keyStats?.updatedAt, formatDatetime],
  ];

  const rightRows: RowData[] = [
    [t('Gross Profit'), financial?.grossProfit, formatLargeMoney],
    [t('Total Revenue'), financial?.totalRevenue, formatLargeMoney],
    [t('Net Income'), financial?.netIncome, formatLargeMoney],
    [t('Current Assets'), financial?.currentAssets, formatLargeMoney],
    [t('Total Assets'), financial?.totalAssets, formatLargeMoney],
    [t('Total Liabilities'), financial?.totalLiabilities, formatLargeMoney],
    [t('Current Cash'), financial?.currentCash, formatLargeMoney],
    [t('Current Debt'), financial?.currentDebt, formatLargeMoney],
    [t('Total Cash'), financial?.totalCash, formatLargeMoney],
    [t('Total Debt'), financial?.totalDebt, formatLargeMoney],
    [t('Cash Flow'), financial?.cashFlow, formatLargeMoney],
    [t(UPDATED_AT), financial?.updatedAt, formatDatetime],
  ];

  return (
    <div>
      {profile && (
        <Row>
          <Col>
            <p>{profile.description}</p>
          </Col>
        </Row>
      )}
      <Row>
        <Col md={6}>
          <Table bordered size="sm" style={{ fontSize: '0.8rem' }}>
            <tbody>{leftRows.map(mapper)}</tbody>
          </Table>
        </Col>
        <Col md={6}>
          <Table bordered size="sm" style={{ fontSize: '0.8rem' }}>
            <tbody>{rightRows.map(mapper)}</tbody>
          </Table>
        </Col>
      </Row>
    </div>
  );
};

const StockProfile: React.FC<Props> = ({ category, ticker, language }) => {
  const { data, isFetching, error } = useQuery(
    ['queryStockProfile', category, ticker, language],
    () => stockService.queryStockProfile(category, ticker, language)
  );

  const { t } = useTranslation();

  const getErrorMessage = useErrorMessage();

  if (isFetching) return <LoadingImage loading={true} displayAsBlock={true} />;
  if (error) return <p>{getErrorMessage(error)}</p>;
  if (!data) return <p>{t('No data available')}</p>;

  return (
    <>
      {category === 'HK' ? (
        <HKProfile profile={data} />
      ) : (
        <USProfile profile={data} />
      )}
      <Row>
        <Col className="text-center">
          <small
            style={{
              fontSize: '0.8rem',
              color: 'gray',
            }}
          >
            {t(UPDATED_AT)}{' '}
            {uiHelper.formatDateTimeLocalFromTimestamp(data.updatedAt)}
          </small>
        </Col>
      </Row>
    </>
  );
};

export default StockProfile;
